import type { MouseEvent } from "react";
import MenuItem, { type MenuItemProps } from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import CircularProgress from "@mui/material/CircularProgress";
import { useBusyClick } from "../../hooks/useBusyClick";
import {
  type ConfirmDeleteProp,
  useConfirmableClick,
} from "../../hooks/useConfirmableClick";

export type AppMenuItemProps = MenuItemProps & {
  loading?: boolean;
  busyIgnore?: boolean;
  /** When set, shows a delete confirmation dialog before running onClick. */
  confirmDelete?: ConfirmDeleteProp;
};

/** MenuItem with the same auto-busy behavior as AppButton. */
export default function AppMenuItem({
  loading = false,
  busyIgnore = false,
  confirmDelete,
  disabled,
  onClick,
  children,
  ...rest
}: AppMenuItemProps) {
  const confirmedOnClick = useConfirmableClick(onClick, confirmDelete);
  const { busy, onClick: busyOnClick } = useBusyClick<MouseEvent<HTMLLIElement>>(
    confirmedOnClick,
    {
      loading,
      disabled: !!disabled,
      busyIgnore,
    }
  );
  const showBusy = busy && !busyIgnore;

  return (
    <MenuItem
      disabled={disabled || showBusy}
      onClick={busyOnClick}
      aria-busy={showBusy || undefined}
      {...rest}
    >
      {showBusy && (
        <ListItemIcon sx={{ minWidth: 28 }}>
          <CircularProgress color="inherit" size={14} />
        </ListItemIcon>
      )}
      {children}
    </MenuItem>
  );
}
